import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, fontSize, fontWeight, borderRadius, spacing, lineHeightRatio, contentWidth} from '@/styles/tokens';
import {cdnUrl} from '@/util/cdnImage';
import {renderParts} from './richText';
import type {approachSectionContent} from '@/util/contents/drawpost-data';

interface ApproachSectionProps {
    heading: string;
    subheading: string;
    content: typeof approachSectionContent;
}

const sectionSx = {
    bgcolor: colors.background.subtle
} as const;

const headingSx = {
    fontSize: {xs: fontSize[28], sm: fontSize[36]},
    color: colors.text.primary,
    mb: 2,
    lineHeight: lineHeightRatio.tight
} as const;

const subHeadingSx = {
    fontSize: fontSize[20],
    color: colors.accent.indigo,
    mb: 4
} as const;

const bulletItemSx = {
    display: 'flex',
    gap: spacing[16],
    alignItems: 'flex-start',
    mb: 3
} as const;

const bulletTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeightRatio.looser
} as const;

const bulletIconWrapSx = {
    width: spacing[48],
    height: spacing[48],
    borderRadius: borderRadius.xl,
    bgcolor: colors.accent.indigo,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const screenshotSx = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    maxWidth: contentWidth.screenshot,
    aspectRatio: '972/798',
    mt: {xs: 5, md: 0}
} as const;

const ApproachSection = memo(function ApproachSection({heading, subheading, content}: ApproachSectionProps) {
    const screenshotImage = cdnUrl(content.screenshotPath);

    return (
        <Box sx={sectionSx}>
            <MUIContainer>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography component="h2" sx={headingSx} className="font-headers">
                            {heading}
                        </Typography>

                        <Typography component="h3" sx={subHeadingSx} className="font-headers">
                            {subheading}
                        </Typography>

                        {content.points.map((point, index) => (
                            <Box key={index} sx={bulletItemSx}>
                                <Box sx={bulletIconWrapSx}>
                                    <Image
                                        src={cdnUrl(point.iconSrc)}
                                        alt=""
                                        aria-hidden="true"
                                        width={20}
                                        height={20}
                                        style={{filter: 'brightness(0) invert(1)'}}
                                    />
                                </Box>
                                <Typography sx={bulletTextSx} className="font-regular-text remove-underline">
                                    {renderParts(point.parts)}
                                </Typography>
                            </Box>
                        ))}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={screenshotSx}>
                            <Image
                                src={screenshotImage}
                                alt={content.screenshotAlt}
                                fill
                                loading="lazy"
                                style={{objectFit: 'cover'}}
                                sizes="(max-width: 900px) 100vw, 50vw"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
});

export default ApproachSection;
